import { FC } from 'react';
import { Tag } from '@fe-learner-common';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faCodeMerge } from '@fortawesome/pro-regular-svg-icons/faCodeMerge';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons/faExclamationCircle';
import { faHourglass } from '@fortawesome/pro-regular-svg-icons/faHourglass';
import { faKey } from '@fortawesome/pro-regular-svg-icons/faKey';
import { faQuestion } from '@fortawesome/pro-regular-svg-icons/faQuestion';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons/faCircleExclamation';
import { cn, FaIcon } from '@fe-learner-common';

export type TEventTags =
  | 'ongoingEvent'
  | 'pastEvent'
  | 'optional'
  | 'completion_status_completed'
  | 'completion_status_completed_passed'
  | 'completion_status_completed_awaiting_evaluation'
  | 'completion_status_completed_failed'
  | 'completion_status_not_completed'
  | 'completion_status_unknown'
  | 'pin';

export type TTag = Record<TEventTags, string>;
export type EventTagsProps = {
  tags?: TTag[] | null;
  texts: Record<TEventTags, string>;
};

type TagProps = {
  className?: string;
  icon?: IconDefinition;
};

const tagMap: Map<TEventTags, TagProps> = new Map([
  [
    'ongoingEvent',
    {
      className: 'lf:bg-prominence-50 lf:text-prominence-500 ',
      icon: faSpinner,
    },
  ],
  ['pastEvent', { icon: faExclamationCircle }],
  [
    'optional',
    { className: 'lf:bg-prominence-50 lf:text-prominence-500 ', icon: faCodeMerge },
  ],
  [
    'completion_status_completed',
    { icon: faCheck, className: 'lf:bg-positive-50 lf:text-positive-500 ' },
  ],
  [
    'completion_status_completed_passed',
    { icon: faCheck, className: 'lf:bg-positive-50 lf:text-positive-500 ' },
  ],
  [
    'completion_status_completed_failed',
    {
      icon: faCircleExclamation,
      className: 'lf:bg-negative-50 lf:text-negative-500 ',
    },
  ],
  ['completion_status_completed_awaiting_evaluation', { icon: faHourglass }],
  ['completion_status_not_completed', { icon: faHourglass }],

  ['completion_status_unknown', { icon: faQuestion }],
  ['pin', { className: 'lf:uppercase', icon: faKey }],
]);

export const EventTags: FC<EventTagsProps> = ({ tags, texts }) => {
  if (!tags || !texts) return null;
  return (
    <div className="lf:flex lf:gap-2">
      {tags.map((ttag) => {
        const [key, value] = Object.entries(ttag)[0];
        const tagProps = tagMap.get(key as TEventTags);
        return (
          <Tag key={key} className={cn('', tagProps?.className)}>
            {tagProps?.icon && (
              <FaIcon icon={tagProps.icon} width={12} height={12} />
            )}
            {texts?.[key as TEventTags]}
            {value && `: ${value}`}
          </Tag>
        );
      })}
    </div>
  );
};
